import type { ReactNode } from 'react';

export interface SegTab {
  value: string;
  label: string;
  /** Optional count badge (null = hide badge). */
  count?: number | null;
  icon?: ReactNode;
}

interface BaseProps {
  tabs: SegTab[];
  /** Accessible label for the tablist / group. */
  label?: string;
  className?: string;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (v: string) => void;
}

interface MultiProps extends BaseProps {
  multiple: true;
  values: string[];
  onChange: (v: string[]) => void;
}

type Props = SingleProps | MultiProps;

/**
 * Underline tab bar with optional count badges. The canonical idiom for primary
 * filters — status pipelines, section switchers, console views.
 *
 * Single-select by default (radio-style tablist). Pass `multiple` for a
 * multi-select filter: tabs toggle on/off, the leading "All" tab (value '')
 * means "no constraint" and is active exactly when `values` is empty. Multi mode
 * renders as toggle buttons (`aria-pressed`) rather than a tablist, since a
 * tablist with several selected tabs is non-conformant.
 */
export default function SegmentedTabs(props: Props) {
  const { tabs, label, className = '' } = props;

  const isActive = (t: SegTab): boolean =>
    props.multiple
      ? t.value === ''
        ? props.values.length === 0
        : props.values.includes(t.value)
      : props.value === t.value;

  const handle = (t: SegTab) => {
    if (!props.multiple) return props.onChange(t.value);
    if (t.value === '') return props.onChange([]); // "All" clears
    props.onChange(
      props.values.includes(t.value)
        ? props.values.filter((v) => v !== t.value)
        : [...props.values, t.value],
    );
  };

  return (
    <div className={`border-b border-gray-200 dark:border-gray-700 ${className}`}>
      <div
        role={props.multiple ? 'group' : 'tablist'}
        aria-label={label}
        className="flex items-center gap-1 overflow-x-auto pb-px"
      >
        {tabs.map((t) => {
          const active = isActive(t);
          return (
            <button
              key={t.value || '__all'}
              type="button"
              role={props.multiple ? undefined : 'tab'}
              aria-selected={props.multiple ? undefined : active}
              aria-pressed={props.multiple ? active : undefined}
              onClick={() => handle(t)}
              className={`flex min-h-[44px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:bg-primary-50 dark:focus-visible:bg-primary-900/20 sm:min-h-0 sm:py-2 ${
                active
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t.icon}
              {t.label}
              {t.count != null && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs ${
                    active
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {t.count.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
